import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import DynamicTable from "../../components/common/DynamicTable";
import config from "../../config.js";
import TitleBar from "../navbar/TitleBar";
import NavMenu from "../navbar/NavMenu";
import { useTranslation } from "react-i18next";
import Footer from "../common/Footer.jsx";
import {
  buildVehicleScheduleConflictMessage,
  getGenericApiErrorMessage,
} from "../../utils/scheduleUi";

export default function ScheduleVehicle() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Extract vehicleId from query parameters
  const queryParams = new URLSearchParams(location.search);
  const vehicleId = queryParams.get("vehicleId");

  const [schedules, setSchedules] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [vehicleData, setVehicleData] = useState({ name: "-", routeName: "-" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [tableData, setTableData] = useState([]);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  const formatRepetition = (repetition) => {
    if (!repetition) return "-";
    // Convert "DAILY" to "Daily", "WEEKLY" to "Weekly", etc.
    return (
      repetition.charAt(0).toUpperCase() + repetition.slice(1).toLowerCase()
    );
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "-";

    const date = new Date(dateTime);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Headers for the dynamic table
  const headers = [
    { key: "Name", value: "Name" },
    { key: "Description", value: "Description" },
    { key: "Start Time", value: "Start Time" },
    { key: "End Time", value: "End Time" },
    { key: "Repetition", value: "Repetition" },
    { key: "Assign", value: "Assign" },
  ];

  // Filter configuration - UPDATED: Use uppercase values to match the data
  // const filterConfig = [
  // {
  //   id: "repitation",
  //   label: "Repetition",
  //   options: [
  //     { value: "DAILY", label: "Daily" },
  //     { value: "WEEKLY", label: "Weekly" },
  //     { value: "MONTHLY", label: "Monthly" },
  //     { value: "ONETIME", label: "Onetime" },
  //   ],
  // },

  // ];

  const filterConfig = React.useMemo(() => {
    if (!schedules || schedules.length === 0) return [];

    return [
      {
        id: "repitation",
        label: "Repetition",
        options: [
          ...Array.from(new Set(schedules.map((s) => s.repitation)))
            .filter(Boolean) // Remove null/undefined
            .sort() // Sort alphabetically
            .map((value) => ({
              value: value, // Keep original uppercase for filtering
              label: formatRepetition(value), // Display formatted
            })),
        ],
      },
    ];
  }, [schedules]);

  useEffect(() => {
    // If no vehicleId is provided, redirect back
    if (!vehicleId) {
      navigate("/vehicles"); // Redirect to vehicles list
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        setSubmitError(null);

        if (!orgId) {
          setSchedules([]);
          setLoading(false);
          return;
        }

        // Fetch schedules
        const schedulesResponse = await axios.get(
          `${config.baseURL}/schedule/organization`,
          {
            params: { organizationId: orgId },
            withCredentials: true,
          }
        );

        console.log("Fetched schedules:", schedulesResponse.data);
        setSchedules(schedulesResponse.data || []);

        // Fetch vehicle data and pre-selected schedules
        const vehicleResponse = await axios.get(
          `${config.baseURL}/vehicle/id`,
          {
            params: {
              vehicleId,
              organizationId: orgId,
            },
            withCredentials: true,
          }
        );

        // Set vehicle data
        setVehicleData({
          name: vehicleResponse.data.vehicle.name || "-",
          routeName: vehicleResponse.data.vehicle.route?.name || "-",
        });

        // Set pre-selected schedules
        const preSelectedSchedules = {};
        if (
          vehicleResponse.data.vehicle.vehicleSchedule &&
          Array.isArray(vehicleResponse.data.vehicle.vehicleSchedule)
        ) {
          vehicleResponse.data.vehicle.vehicleSchedule.forEach((vs) => {
            preSelectedSchedules[vs.scheduleId] = true;
          });
        }
        setSelectedSchedules(preSelectedSchedules);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Only set error if it's a real API failure, not empty data
        if (error.response && error.response.status >= 500) {
          setLoadError("Failed to load schedules. Please try again.");
        } else {
          // For other errors (like 404 or empty responses), just show empty state
          setSchedules([]);
          setLoadError(null);
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [vehicleId, orgId, navigate]);

  // Update table data based on schedules, search, and filters
  useEffect(() => {
    let filteredData = [...schedules];

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter(
        (schedule) =>
          schedule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          schedule.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "All") {
        // Filter by the field that matches the key
        filteredData = filteredData.filter((schedule) => {
          console.log(`Comparing ${schedule[key]} with ${value}`);
          return schedule[key] === value;
        });
      }
    });

    console.log("Filtered data:", filteredData);

    // Transform data for DynamicTable
    const transformedData = filteredData.map((schedule) => {
      return {
        Name: schedule.name,
        Description: schedule.description,
        "Start Time": formatDateTime(schedule.startTime),
        "End Time": formatDateTime(schedule.endTime),
        Repetition: formatRepetition(schedule.repitation),
        Assign: (
          <Checkbox
            style={{ color: "rgba(32, 168, 224, 1)" }}
            checked={selectedSchedules[schedule.id] || false}
            onChange={() => handleCheckboxChange(schedule.id)}
          />
        ),
      };
    });

    setTableData(transformedData);
  }, [schedules, searchTerm, filters, selectedSchedules]);

  const handleCheckboxChange = (scheduleId) => {
    setSubmitError(null);
    setSelectedSchedules((prev) => ({
      ...prev,
      [scheduleId]: !prev[scheduleId],
    }));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (newFilters) => {
    console.log("New filters:", newFilters);
    setFilters(newFilters);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setSubmitError(null);

      // Get selected schedule IDs
      const selectedIds = Object.keys(selectedSchedules)
        .filter((id) => selectedSchedules[id])
        .map((id) => parseInt(id));

      // Save the selected schedules to the vehicle using the PATCH endpoint
      await axios.patch(
        `${config.baseURL}/vehicle/schedule`,
        {
          vehicleId: parseInt(vehicleId),
          scheduleIds: selectedIds,
        },
        { withCredentials: true }
      );

      // Navigate back to vehicle details
      navigate(-1, { state: vehicleId });
    } catch (error) {
      console.error("Error saving schedules:", error);
      setSubmitError(
        buildVehicleScheduleConflictMessage(error, {
          vehicleName: vehicleData.name,
          fallbackMessage: getGenericApiErrorMessage(
            error,
            "Failed to save schedules. Please try again.",
          ),
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to vehicle details
    navigate(`/vehicle/add`, { state: vehicleId });
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  const normalizedVehicleId = Number(vehicleId);
  const subtitleBreadcrumbHandlers =
    vehicleData.name && vehicleData.name !== "-"
      ? {
          [vehicleData.name]: {
            to: "/vehicle/add",
            state: {
              vehicleId: Number.isNaN(normalizedVehicleId)
                ? vehicleId
                : normalizedVehicleId,
            },
          },
        }
      : {};

  if (loading)
    return (
      <>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title="Assign Schedules"
          path={`${orgName} > Vehicles > Update Vehicle > Assign Schedule`}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );

  if (loadError)
    return (
      <>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title="Assign Schedules"
          path="Vehicles > Update Vehicle > Assign Schedule"
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Typography color="error">{loadError}</Typography>
        </Box>
      </>
    );

  return (
    <>
      <TitleBar />
      <NavMenu />
      <SubTitle
        title={`Assign Schedules to ${vehicleData.name} `}
        path={`${orgName} > Vehicles > ${vehicleData.name} > Assign Schedules`}
        breadcrumbHandlers={subtitleBreadcrumbHandlers}
      />

      <Box
        sx={{
          display: "flex",
          height: "60px",
          justifyContent: "space-between",
          padding: "12px 16px",
          margin: "0px 40px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 2px 5px 0px #00000040",
        }}
      >
        <Box
          sx={{
            fontWeight: 500,
            fontFamily: "Roboto",
            fontSize: "20px",
            lineHeight: "100%",
            letterSpacing: "0%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#414958",
              padding: "2px 6px",
              borderRadius: "2px",
            }}
          >
            {t("Vehicle")} :
          </span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "Roboto",
              fontSize: "20px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#183059",
              padding: "2px 6px",
              borderRadius: "2px",
              marginLeft: "4px",
            }}
          >
            {vehicleData.name}
          </span>
        </Box>

        <Box
          sx={{
            fontWeight: 500,
            fontFamily: "Roboto",
            fontSize: "20px",
            lineHeight: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#414958",
              padding: "2px 6px",
              borderRadius: "2px",
            }}
          >
            {t("Route")}:
          </span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "Roboto",
              fontSize: "20px",
              lineHeight: "100%",
              color: "#183059",
              padding: "2px 6px",
              borderRadius: "2px",
              marginLeft: "4px",
            }}
          >
            {vehicleData.routeName}
          </span>
        </Box>
      </Box>

      <Box
        sx={{
          margin: "20px 40px",
          borderRadius: "10px",
          padding: "24px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 1px 5px 0px #00000040",
        }}
      >
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="Search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <DynamicTable headers={headers} data={tableData} loading={loading} />

        {!loading && tableData.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px",
              color: "#666",
              fontSize: "18px",
            }}
          >
            <Typography>No schedules available to assign</Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 3,
            gap: 2,
          }}
        >
          <button
            onClick={handleCancel}
            style={{
              width: "122px",
              fontSize: "20px",
              color: "#1F242E",
              height: "55px",
              borderRadius: "8px",
              textTransform: "none",
              padding: "16px 24px",
              background: "#F2F2F3",
              border: "1px solid #414958",
            }}
          >
            {t("Cancel")}
          </button>

          <button
            onClick={handleSubmit}
            style={{
              width: "125px",
              height: 55,
              fontSize: "20px",
              color: "#1F242E",
              textTransform: "none",
              borderRadius: "8px",
              padding: "16px 24px",
              background: "#F9E719",
              border: "1px solid #414958",
            }}
          >
            {t("Save")}
          </button>
        </Box>
      </Box>
      <Footer />
    </>
  );
}
